/**
 * Detalhe de uma transcricao: metadados, texto/segmentos, edicao manual,
 * extracao via IA (Claude).
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import GerarVideoModal from '../components/GerarVideoModal';
import {
  ArrowLeft, ExternalLink, Edit2, Save, X, Trash2, Copy, Sparkles, Loader2, Film,
  Youtube, Instagram, PlayCircle, Globe, Activity, Pause as PauseIcon,
} from 'lucide-react';
import type { Emotion } from '../lib/cena';

interface MarcacaoCadencia {
  segmentoIdx: number;
  start: number;
  end: number;
  texto: string;
  charsPorSeg: number;
  velocidade: 'lento' | 'normal' | 'rapido';
  speedSugerido: number;
  emocaoSugerida: Emotion;
  pausaDepois: number | null;
}

interface CadenciaResp {
  cadencia: {
    marcacoes: MarcacaoCadencia[];
    pausasLongas: Array<{ pos: number; duracao: number; tipo: 'segmento' | 'palavra' }>;
    duracaoTotal: number;
    charsPorSegMedio: number;
    temPalavras: boolean;
  };
}

type Fonte = 'youtube' | 'instagram' | 'panda_video' | 'generic';

interface Segmento {
  start: number;
  end: number;
  text: string;
}

interface TranscricaoFull {
  id: string;
  urlOrigem: string;
  fonte: Fonte;
  titulo: string | null;
  descricao: string | null;
  autor: string | null;
  autorUrl: string | null;
  thumbnailUrl: string | null;
  duracaoSeg: number | null;
  publicadoEm: string | null;
  idioma: string | null;
  textoCompleto: string;
  segmentos: Segmento[] | null;
  comQuem: string[];
  resumo: string | null;
  topicos: string[];
  custoUsd: string | number | null;
  modeloWhisper: string | null;
  metadata: Record<string, unknown> | null;
  criadoEm: string;
  job: { id: string; status: string; criadoEm: string } | null;
}

const FONTE_ICON: Record<Fonte, typeof Youtube> = {
  youtube: Youtube,
  instagram: Instagram,
  panda_video: PlayCircle,
  generic: Globe,
};

const FONTE_LABEL: Record<Fonte, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  panda_video: 'Panda Video',
  generic: 'Genérico',
};

export default function Detalhe() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [t, setT] = useState<TranscricaoFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [extraindo, setExtraindo] = useState(false);
  const [gerarVideoAberto, setGerarVideoAberto] = useState(false);

  const [tituloEdit, setTituloEdit] = useState('');
  const [autorEdit, setAutorEdit] = useState('');
  const [comQuemEdit, setComQuemEdit] = useState('');
  const [resumoEdit, setResumoEdit] = useState('');
  const [topicosEdit, setTopicosEdit] = useState('');

  const [cadencia, setCadencia] = useState<CadenciaResp['cadencia'] | null>(null);
  const [carregandoCadencia, setCarregandoCadencia] = useState(false);

  async function carregar() {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get<TranscricaoFull>(`/transcricoes/${id}`);
      setT(r);
      setTituloEdit(r.titulo ?? '');
      setAutorEdit(r.autor ?? '');
      setComQuemEdit(r.comQuem.join(', '));
      setResumoEdit(r.resumo ?? '');
      setTopicosEdit(r.topicos.join(', '));
      setErro(null);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [id]);

  async function salvar() {
    if (!id) return;
    try {
      await api.patch(`/transcricoes/${id}`, {
        titulo: tituloEdit || null,
        autor: autorEdit || null,
        comQuem: comQuemEdit.split(',').map((s) => s.trim()).filter(Boolean),
        resumo: resumoEdit || null,
        topicos: topicosEdit.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setEditando(false);
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function deletar() {
    if (!id) return;
    if (!confirm('Mover esta transcrição para a lixeira (soft delete)?')) return;
    try {
      await api.delete(`/transcricoes/${id}`);
      nav('/');
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function copiarTexto() {
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t.textoCompleto);
      alert('Texto copiado.');
    } catch {
      alert('Não foi possível copiar.');
    }
  }

  async function extrairViaIA() {
    if (!id || extraindo) return;
    setExtraindo(true);
    try {
      await api.post(`/transcricoes/${id}/extrair-participantes`, {});
      await carregar();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setExtraindo(false);
    }
  }

  async function carregarCadencia() {
    if (!id || carregandoCadencia) return;
    setCarregandoCadencia(true);
    try {
      const r = await api.get<CadenciaResp>(`/transcricoes/${id}/cadencia`);
      setCadencia(r.cadencia);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setCarregandoCadencia(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Carregando...</div>;
  if (erro || !t) return (
    <div className="p-6">
      <Link to="/" className="text-sm text-purple-600 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400">
        {erro || 'Transcrição não encontrada.'}
      </div>
    </div>
  );

  const Icon = FONTE_ICON[t.fonte];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <Link to="/" className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <div className="flex gap-2">
          <button
            onClick={copiarTexto}
            className="flex items-center gap-1 text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900"
          >
            <Copy className="w-3.5 h-3.5" /> Copiar texto
          </button>
          {!editando && (
            <button
              onClick={extrairViaIA}
              disabled={extraindo}
              className="flex items-center gap-1 text-xs px-2 py-1.5 border border-purple-300 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-50 dark:bg-purple-900/30 disabled:opacity-60"
              title="Usa Claude para extrair participantes, tópicos e resumo"
            >
              {extraindo ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Extraindo…</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5" /> Extrair via IA</>
              )}
            </button>
          )}
          {!editando && (
            <button
              onClick={() => setGerarVideoAberto(true)}
              className="flex items-center gap-1 text-xs px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded"
              title="Gera vídeo do avatar HeyGen falando este texto"
            >
              <Film className="w-3.5 h-3.5" /> Gerar vídeo
            </button>
          )}
          {!editando && (
            <button
              onClick={() => setEditando(true)}
              className="flex items-center gap-1 text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900"
            >
              <Edit2 className="w-3.5 h-3.5" /> Editar
            </button>
          )}
          {editando && (
            <>
              <button
                onClick={() => setEditando(false)}
                className="flex items-center gap-1 text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900"
              >
                <X className="w-3.5 h-3.5" /> Cancelar
              </button>
              <button
                onClick={salvar}
                className="flex items-center gap-1 text-xs px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded"
              >
                <Save className="w-3.5 h-3.5" /> Salvar
              </button>
            </>
          )}
          {!editando && (
            <button
              onClick={deletar}
              className="flex items-center gap-1 text-xs px-2 py-1.5 border border-red-300 text-red-700 dark:text-red-400 rounded hover:bg-red-50 dark:bg-red-900/20"
            >
              <Trash2 className="w-3.5 h-3.5" /> Apagar
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
        <div className="flex gap-4">
          {t.thumbnailUrl ? (
            <img src={t.thumbnailUrl} alt="" className="w-48 h-28 object-cover rounded bg-gray-100 dark:bg-gray-700 shrink-0" />
          ) : (
            <div className="w-48 h-28 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center shrink-0">
              <Icon className="w-10 h-10 text-gray-400 dark:text-gray-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {editando ? (
              <input
                value={tituloEdit}
                onChange={(e) => setTituloEdit(e.target.value)}
                className="w-full text-xl font-bold border-b border-gray-300 dark:border-gray-600 focus:border-purple-500 outline-none mb-2"
                placeholder="Título"
              />
            ) : (
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 line-clamp-2 mb-2">
                {t.titulo || '(sem título)'}
              </h1>
            )}

            <div className="text-sm text-gray-600 dark:text-gray-400 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1">
                <Icon className="w-4 h-4" />
                {FONTE_LABEL[t.fonte]}
              </span>
              {editando ? (
                <>
                  <span>·</span>
                  <input
                    value={autorEdit}
                    onChange={(e) => setAutorEdit(e.target.value)}
                    placeholder="autor"
                    className="border-b border-gray-300 dark:border-gray-600 focus:border-purple-500 outline-none px-1 text-sm"
                  />
                </>
              ) : t.autor ? (
                <>
                  <span>·</span>
                  {t.autorUrl ? (
                    <a href={t.autorUrl} target="_blank" rel="noopener noreferrer" className="hover:text-purple-600">{t.autor}</a>
                  ) : <span>{t.autor}</span>}
                </>
              ) : null}
              {t.duracaoSeg !== null && <><span>·</span><span>{formatarDuracao(t.duracaoSeg)}</span></>}
              {t.idioma && <><span>·</span><span className="uppercase">{t.idioma}</span></>}
              {t.publicadoEm && <><span>·</span><span>publicado {formatarData(t.publicadoEm)}</span></>}
            </div>

            <div className="mt-2 flex gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
              {t.modeloWhisper && (<span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{t.modeloWhisper}</span>)}
              {t.custoUsd && (<span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{formatarCusto(t.custoUsd)}</span>)}
              <a
                href={t.urlOrigem}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded hover:bg-purple-100 hover:text-purple-700 dark:text-purple-300 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> origem
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Com quem</h3>
          {editando ? (
            <input
              value={comQuemEdit}
              onChange={(e) => setComQuemEdit(e.target.value)}
              placeholder="separar por vírgula"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
            />
          ) : t.comQuem.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {t.comQuem.map((p, i) => (
                <span key={i} className="px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">{p}</span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
              Vazio. Use <Sparkles className="inline w-3 h-3 text-purple-500" /> Extrair via IA acima para preencher.
            </p>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Tópicos</h3>
          {editando ? (
            <input
              value={topicosEdit}
              onChange={(e) => setTopicosEdit(e.target.value)}
              placeholder="separar por vírgula"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
            />
          ) : t.topicos.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {t.topicos.map((p, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">{p}</span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">—</p>
          )}
        </div>
      </div>

      {(editando || t.resumo) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Resumo</h3>
          {editando ? (
            <textarea
              value={resumoEdit}
              onChange={(e) => setResumoEdit(e.target.value)}
              rows={3}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
            />
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300">{t.resumo}</p>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Transcrição</h2>
          <div className="flex items-center gap-3">
            {t.segmentos && t.segmentos.length > 0 && (
              cadencia ? (
                <span className="text-xs text-purple-700 dark:text-purple-400 flex items-center gap-1">
                  <Activity className="w-3.5 h-3.5" />
                  Cadência detectada{cadencia.temPalavras ? ' (word-level)' : ''} · {cadencia.charsPorSegMedio} chars/s · {cadencia.pausasLongas.length} pausas
                </span>
              ) : (
                <button
                  onClick={carregarCadencia}
                  disabled={carregandoCadencia}
                  className="text-xs px-2 py-1 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-1 disabled:opacity-50"
                  title="Detecta pausas, velocidade e emoção sugerida"
                >
                  {carregandoCadencia
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Activity className="w-3.5 h-3.5" />
                  }
                  Detectar cadência
                </button>
              )
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t.textoCompleto.length.toLocaleString()} caracteres
              {t.segmentos && ` · ${t.segmentos.length} segmentos`}
            </span>
          </div>
        </div>

        {t.segmentos && t.segmentos.length > 0 ? (
          <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-2">
            {t.segmentos.map((s, i) => {
              const m = cadencia?.marcacoes[i];
              return (
                <div key={i}>
                  <div className="flex gap-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 p-1 rounded items-start">
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-mono shrink-0 w-16 mt-0.5">
                      {formatarTimestamp(s.start)}
                    </span>
                    <span className="text-gray-800 dark:text-gray-200 flex-1">{s.text}</span>
                    {m && (
                      <div className="flex items-center gap-1 shrink-0 text-[10px] uppercase font-semibold">
                        <span
                          className={`px-1.5 py-0.5 rounded ${
                            m.velocidade === 'rapido' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            : m.velocidade === 'lento' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                          title={`${m.charsPorSeg} chars/s · speed sugerido ${m.speedSugerido}x`}
                        >
                          {m.velocidade}
                        </span>
                        <span
                          className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                          title="Emoção sugerida (heurística por pontuação)"
                        >
                          {m.emocaoSugerida}
                        </span>
                      </div>
                    )}
                  </div>
                  {m?.pausaDepois !== null && m?.pausaDepois !== undefined && m.pausaDepois >= 0.4 && (
                    <div className="flex gap-3 items-center pl-16 text-xs text-gray-400 dark:text-gray-500">
                      <PauseIcon className="w-3 h-3" />
                      pausa {m.pausaDepois.toFixed(2)}s
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
            {t.textoCompleto}
          </p>
        )}
      </div>

      {gerarVideoAberto && (
        <GerarVideoModal
          transcricaoId={t.id}
          textoCompleto={t.textoCompleto}
          resumo={t.resumo}
          onClose={() => setGerarVideoAberto(false)}
        />
      )}
    </div>
  );
}

function formatarDuracao(seg: number): string {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}min`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatarTimestamp(seg: number): string {
  const m = Math.floor(seg / 60);
  const s = Math.floor(seg % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatarCusto(v: string | number): string {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return '';
  return `$${n.toFixed(4)}`;
}
